import { initializePaddle, type Paddle } from '@paddle/paddle-js';
import { env } from '@/lib/env';

/**
 * Lazy, memoized Paddle.js bootstrap.
 *
 * `@paddle/paddle-js`'s `initializePaddle` injects the Paddle CDN script and
 * resolves a `Paddle` instance. We only ever want to do that once per page, and
 * only when the user actually reaches an upgrade surface — so the init promise
 * is created on the first `getPaddle()` call and cached at module scope for
 * every later caller.
 *
 * When `VITE_PADDLE_CLIENT_TOKEN` is unset (local dev without Paddle keys),
 * `getPaddle()` resolves `undefined` and never touches the network — callers
 * disable the checkout button via `isPaddleConfigured()` instead of crashing.
 */
let paddlePromise: Promise<Paddle | undefined> | null = null;

export function isPaddleConfigured(): boolean {
  return env.paddleClientToken.length > 0;
}

export function getPaddle(): Promise<Paddle | undefined> {
  if (!isPaddleConfigured()) return Promise.resolve(undefined);
  if (!paddlePromise) {
    paddlePromise = initializePaddle({
      token: env.paddleClientToken,
      environment: env.paddleEnvironment,
    });
  }
  return paddlePromise;
}
